import React from 'react';
import ReactSelect, { components as ReactSelectComponents } from 'react-select';
import { variables } from 'src/Tenants';
import Loader from '../../Loader';
import Button from '../../Button';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Skeleton from 'src/components/Skeleton/Skeleton';
import Callout from 'src/components/InformationCallout/Callout';
import ServiceRequestFormDialog from '../ServiceRequestFormDialog';
import {
  SuiteSelectOption,
  useServiceRequestFormSuiteOrchestrator,
  UseServiceRequestFormSuiteOrchestratorReturn,
} from './useServiceRequestFormSuiteOrchestrator';
import './ServiceRequestFormSuite.scss';

interface ServiceRequestFormSuiteProps {
  isLoadingOptions: boolean;
  optionsError: string | null;
}

const reactSelectAlignStyles = {
  singleValue: (base: any) => ({ ...base, textAlign: 'left' }),
  placeholder: (base: any) => ({ ...base, textAlign: 'left' }),
  input: (base: any) => ({ ...base, textAlign: 'left' }),
  option: (base: any) => ({ ...base, textAlign: 'left' }),
};

const OPTION_SELECTED_BG = '#2684ff';
const OPTION_HOVER_BG = '#f2f2f2';

const suiteSelectStyles = {
  ...reactSelectAlignStyles,
  option: (base: any, state: any) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    textAlign: 'left',
    backgroundColor: state.isSelected
      ? OPTION_SELECTED_BG
      : state.isFocused
        ? OPTION_HOVER_BG
        : 'transparent',
  }),
};

const renderSuiteOption = (option: SuiteSelectOption, meta: any) => {
  if (meta.context === 'value') {
    return option.number;
  }

  return (
    <>
      <span className="suite-option-number">{option.number}</span>
      <div className="suite-option-pills">
        {option.buildingName ? (
          <CategoryPill text={`Building: ${option.buildingName}`} size="small" />
        ) : null}
        {option.warrantyAddress ? (
          <CategoryPill text={option.warrantyAddress} size="small" />
        ) : null}
        {option.stratalot ? (
          <CategoryPill text={`Lot ${option.stratalot}`} size="small" />
        ) : null}
      </div>
    </>
  );
};

const SuiteStepSkeleton = () => (
  <div className="sr-form-suite-skeleton">
    <div className="sr-form-suite-field">
      <Skeleton variant="text" width="8rem" />
      <Skeleton variant="rect" height="3.8rem" />
    </div>

    <div className="sr-form-suite-field">
      <Skeleton variant="text" width="8rem" />
      <Skeleton variant="rect" height="3.8rem" />
    </div>

    <div className="sr-form-suite-skeleton-preview">
      <Skeleton variant="rect" width="18rem" height="14rem" />
      <div className="sr-form-suite-skeleton-lines">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="45%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="35%" />
      </div>
    </div>
  </div>
);

const ServiceRequestFormSuite = ({
  isLoadingOptions,
  optionsError,
}: ServiceRequestFormSuiteProps) => {
  const {
    propertyOptions,
    suiteOptions,
    selectedPropertyOption,
    selectedSuiteOption,
    selectedProperty,
    selectedSuite,
    selectedSuiteAddress,
    selectedSuiteBuildingName,
    isFetchingSuites,
    propertyDisabled,
    hasSelectedProperty,
    canProceed,
    onSelectProperty,
    onSelectSuite,
    onNext,
    onExit,
  }: UseServiceRequestFormSuiteOrchestratorReturn =
    useServiceRequestFormSuiteOrchestrator();

  if (isLoadingOptions) {
    return (
      <ServiceRequestFormDialog
        title={<Skeleton variant="text" width="24rem" />}
        onExit={onExit}
        confirmOnExit={false}
        footer={<Skeleton variant="rect" width="16rem" height="3.6rem" />}
      >
        <SuiteStepSkeleton />
      </ServiceRequestFormDialog>
    );
  }

  if (optionsError) {
    return (
      <ServiceRequestFormDialog
        title={`Start Your ${variables.serviceRequest}`}
        onExit={onExit}
        confirmOnExit={false}
      >
        <Callout
          variant="error"
          title="We couldn't load the options for this dialogue"
          subtitle="Please close this dialogue and try again. If the issue persists, reach out to support."
        />
      </ServiceRequestFormDialog>
    );
  }

  return (
    <ServiceRequestFormDialog
      title={`Start Your ${variables.serviceRequest}`}
      description="First, tell us which unit this request is for."
      onExit={onExit}
      footer={
        <Button
          color="teal"
          size="small"
          label="Next: Select Room / Item"
          onClick={onNext}
          disabled={!canProceed}
        />
      }
    >
      <div className="sr-form-suite-field">
        <label className="input__label sr-form-suite-label">
          Property
          <span className="input__required" />
        </label>
        <ReactSelect
          value={selectedPropertyOption}
          placeholder="Select Property"
          onChange={onSelectProperty}
          options={propertyOptions}
          isDisabled={propertyDisabled}
          styles={reactSelectAlignStyles}
        />
      </div>

      <div className="sr-form-suite-field">
        <label className="input__label sr-form-suite-label">
          {variables.Suite}
          <span className="input__required" />
        </label>
        <ReactSelect
          value={selectedSuiteOption}
          placeholder={`Select ${variables.Suite}`}
          onChange={onSelectSuite}
          options={suiteOptions}
          formatOptionLabel={renderSuiteOption}
          isDisabled={!hasSelectedProperty}
          isLoading={isFetchingSuites}
          loadingMessage={() => (
            <span className="sr-form-suite-loading">
              <Loader imageHeight="18px" imageWidth="18px" />
              Fetching {variables.suites} for this property…
            </span>
          )}
          noOptionsMessage={() =>
            `No ${variables.suites} found for this property. Make sure ${variables.suites} have been created and try again.`
          }
          components={{
            LoadingIndicator: (props: any) => (
              <span className="sr-form-suite-loading-indicator">
                <ReactSelectComponents.LoadingIndicator {...props} />
                <span>Fetching {variables.suites}…</span>
              </span>
            ),
          }}
          styles={suiteSelectStyles}
        />
      </div>

      <div className="sr-form-suite">
        <div className="sr-form-suite__image">
          {selectedProperty?.image ? (
            <img
              className="sr-form-suite__image-photo"
              src={selectedProperty.image}
              alt=""
            />
          ) : null}
        </div>
        <div className="sr-form-suite__address">
          {selectedProperty ? (
            <>
              <div className="sr-form-suite__title">
                {selectedProperty.name}
                {selectedSuite ? ' / ' + selectedSuite.number : ''}
                {selectedSuiteBuildingName
                  ? ` · Building ${selectedSuiteBuildingName}`
                  : ''}
              </div>
              {selectedSuiteAddress || selectedProperty.addressLine1}
              <br />
              {selectedProperty.city}, {selectedProperty.province}
              <br />
              {selectedProperty.postalCode}
            </>
          ) : (
            <>
              <div className="sr-form-suite__title">Property / Suite</div>
              Street Address
              <br />
              City, Province
              <br />
              Postal Code
            </>
          )}
        </div>
      </div>
    </ServiceRequestFormDialog>
  );
};

export default ServiceRequestFormSuite;